import React, { createContext, memo, useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react';
import { useApi } from '../../../hooks/useApi';
import { useAsync } from '../../../hooks/useAsync';
import getQuoteWithCoverages from './getQuoteWithCoverages';
import { getTotalAllInCostValue } from '../../../utils/getTotalAllInCostValue';

const QuoteDataContext = createContext({ data: null, updating: true, update: () => null });

function useQuoteData() {
  return useContext(QuoteDataContext);
}

const ProvideQuoteData = memo(function ProvideQuoteData(props) {
  const { id, renderLoading, renderEmpty, children } = props;

  const { getQuoteWithId, getApplicationWithId, getCoveragesForProductId, getMacavityForQuote, getQuoteConcentrations } = useApi();

  const [macavity, setMacavity] = useState(null);
  const [maxCoverageLimits, setMaxCoverageLimit] = useState([]);
  const [concentrations, setConcentrations] = useState(null);

  const getQuoteDataAndCoverages = useCallback(
    async (refresh = false) => {
      const quote = await getQuoteWithId(id);
      /* The quote page is the one place a removed Skyward loss should be visible for underwriting review.
       Only a refresh skips the read cache: this is the reload the loss grid runs after a save, and a
       cached answer would be the application from before the write. */
      const application = await getApplicationWithId(quote.applicationId, true, { fresh: refresh === true });
      const productCoverages = await getCoveragesForProductId(quote.product.id);

      const coverageLimits = productCoverages.map(({ id, coverageType }) => {
        const updatedCoverageType = coverageType.charAt(0).toUpperCase() + coverageType.slice(1);
        return {
          id,
          coverageType,
          valueName: updatedCoverageType.match(/[A-Z][a-z]+/g).join(' '),
          maxLimit: 0,
        };
      });

      let macavityData;
      try {
        macavityData = await getMacavityForQuote(quote.id);
        setMacavity(macavityData);
      } catch (error) {
        macavityData = null;
      }

      if (!quote) return null;
      return {
        data: {
          ...getQuoteWithCoverages(quote),
          application,
        },
        productCoverages,
        macavity: macavityData,
        coverageLimits,
      };
    },
    [id, getQuoteWithId, getApplicationWithId, getCoveragesForProductId, getMacavityForQuote, getQuoteConcentrations]
  );

  const { value, status, execute, lastUpdated } = useAsync(getQuoteDataAndCoverages);

  const updateMacavity = useCallback(async () => {
    try {
      const updatedMacavity = await getMacavityForQuote(id);
      setMacavity(updatedMacavity); // Update macavity in state
    } catch (error) {
      console.error('Error updating macavity:', error);
    }
  }, [id, getMacavityForQuote]);

  /* useAsync nulls its value when a call rejects, and this provider answers a null value with the empty
     state — which would replace the whole quote page, including a loss grid holding staged edits a
     partly-failed save left behind. Once a load has succeeded, a later failed refresh leaves the last
     one on screen and the empty state is reserved for never having loaded at all. */
  const lastLoaded = useRef(null);
  if (value) {
    lastLoaded.current = value;
  }
  /* Both candidates are matched on their own quote id against the id being asked for, and neither is
     trusted for being the newer of the two: useAsync holds its previous value all the way through the
     next call, so opening a second quote would render the first one's premium and losses under the
     second's id until that fetch answered. Whichever way this provider ends up with the wrong quote, it
     answers with no quote at all. */
  const isRequestedQuote = (candidate) => !!candidate && !!candidate.data && String(candidate.data.id) === String(id);
  const loadedQuote = isRequestedQuote(value) ? value : isRequestedQuote(lastLoaded.current) ? lastLoaded.current : null;
  const { data = null, productCoverages, coverageLimits } = loadedQuote || {};
  useEffect(() => {
    setMaxCoverageLimit(coverageLimits);
  }, [coverageLimits]);

  const context = useMemo(
    () => ({
      data,
      totalAllInCost: getTotalAllInCostValue(data),
      updating: data && status === 'pending',
      /* What is on screen is the retained copy from before the failed reload, which consumers that let it
         be edited have to know: `update` resolves either way, because useAsync keeps a rejection in this
         status instead of re-throwing it. */
      refreshFailed: status === 'error',
      update: execute,
      updateMacavity,
      productCoverages,
      macavity,
      lastUpdated,
      maxCoverageLimits,
      setMaxCoverageLimit,
      concentrations,
      setConcentrations,
    }),
    [data, status, execute, lastUpdated, macavity, productCoverages, updateMacavity, maxCoverageLimits, concentrations, setConcentrations]
  );

  if (!data && status === 'pending') {
    return renderLoading ? renderLoading() : children;
  }

  if (!data) {
    return renderEmpty ? renderEmpty() : children;
  }

  return <QuoteDataContext.Provider value={context}>{children}</QuoteDataContext.Provider>;
});

export { QuoteDataContext, useQuoteData, ProvideQuoteData };
